// src/assessment2/EvaluationView.js
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as GenerateIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Home as HomeIcon,
  ContentCopy as CopyIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import EvaluationAlert from './EvaluationAlert';
import AssessmentResultModal from './AssessmentResultModal';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const EvaluationView = ({ evaluation, onBack, onViewComparison }) => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [currentApplicants, setCurrentApplicants] = useState([]);
  const [sendingInvitations, setSendingInvitations] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [resendingEmail, setResendingEmail] = useState({});
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  // Reproceso del assessment (sólo super-admin)
  const [reprocessingId, setReprocessingId] = useState(null);
  const [applicantToReprocess, setApplicantToReprocess] = useState(null);

  const [usageData, setUsageData] = useState(null);
  const [canSendToAll, setCanSendToAll] = useState(true);

  const { authToken, showAlert, organization, project, language, user } = useStore.getState();
  const { t } = useTranslation();

  const pendingApplicants = currentApplicants.filter(app => app.status === 'pending');
  const pendingCount = pendingApplicants.length;

  const handleUsageCheckComplete = (data) => {
    setUsageData(data);
    const remaining = data.remaining;
    setCanSendToAll(remaining >= pendingCount);
  };

  useEffect(() => {
    // Recalcular canSendToAll cuando cambien pendingCount o usageData
    if (usageData) {
      const remaining = usageData.remaining || 0;
      const canSend = usageData.unlimited || remaining >= pendingCount;
      setCanSendToAll(canSend);
    }
  }, [pendingCount, usageData]);

  // API Functions
  // Assessment 2: cualquier postulante del proyecto (incluye banco de talento),
  // sin exigir informe psicolaboral (el módulo clásico usa /applicants/with-report).
  const getAllApplicants = async () => {
    const response = await api.get(
      `/applicants?projectId=${project}&organizationId=${organization}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const addApplicantToEvaluation = async (evaluationId, applicantData) => {
    try {
      const response = await api.post(
        `/assessment2/evaluations/${evaluationId}/applicants`,
        applicantData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error in addApplicantToEvaluation:', error);
      throw error;
    }
  };

  const removeApplicantFromEvaluation = async (evaluationId, applicantId) => {
    const response = await api.delete(
      `/assessment2/evaluations/${evaluationId}/applicants/${applicantId}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const sendInvitationAPI = async (evaluationId, applicantId) => {
    const response = await api.put(
      `/assessment2/evaluations/${evaluationId}/applicants/${applicantId}/status`,
      { status: 'sent' },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  // Función para abrir modal de confirmación
  const handleOpenDeleteConfirm = (applicant) => {
    setCandidateToDelete(applicant);
    setDeleteConfirmOpen(true);
  };

  // Función para cerrar modal de confirmación
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setCandidateToDelete(null);
  };

  // Función para confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!candidateToDelete) return;

    try {
      const response = await removeApplicantFromEvaluation(evaluation.id, candidateToDelete.applicantId);

      if (response.response && response.response.status_response === 'OK') {
        setCurrentApplicants(prev => prev.filter(app => app.applicantId !== candidateToDelete.applicantId));
        showAlert(t('evaluationResponses.applicantDeletedSuccessfully'), 'success');
      } else {
        showAlert(t('evaluationResponses.errorDeletingApplicant'), 'error');
      }

    } catch (err) {
      console.error('Error removing applicant:', err);
      showAlert(t('evaluationResponses.errorDeletingApplicant'), 'error');
    } finally {
      handleCloseDeleteConfirm();
    }
  };

  // Nueva función para crear EvaluationResponse
  const createEvaluationResponse = async (responseData) => {
    const response = await api.post(
      "/assessment2/evaluation-responses",
      responseData,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  // useEffect para cargar datos iniciales
  useEffect(() => {
    if (evaluation) {
      loadInitialData();
    }
  }, [evaluation]);

  // Agregar esta función para obtener evaluationResponses
  const getEvaluationResponses = async (evaluationId) => {
    const response = await api.get(
      `/assessment2/evaluation-responses/by-evaluation?evaluationId=${encodeURIComponent(evaluationId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  // Assessment 2 (PLAN_ASSESSMENT2.md): la evaluación GPT + reportes corre en una
  // cola. `processingStatus` viene de la respuesta: 'processing' | 'evaluated' | 'error'.
  // Respuestas anteriores a la cola no traen el campo: se consideran evaluadas si
  // ya tienen overallScore.
  const extractProcessingFields = (evaluationResponse) => {
    if (!evaluationResponse) {
      return { processingStatus: null, processingError: null, hasEvaluation: false };
    }
    const hasScore = evaluationResponse.overallScore !== null && evaluationResponse.overallScore !== undefined;
    const processingStatus = evaluationResponse.processingStatus || (hasScore ? 'evaluated' : null);
    return {
      processingStatus,
      processingError: evaluationResponse.processingError || null,
      hasEvaluation: processingStatus === 'evaluated' || (!processingStatus && hasScore),
    };
  };

  // Refresco liviano (sin spinner): actualiza responseId y estado de procesamiento
  // de los candidatos a partir de las respuestas actuales.
  const refreshResponses = async () => {
    if (!evaluation?.id) return;
    try {
      const responsesResult = await getEvaluationResponses(evaluation.id);
      if (!(responsesResult.response && responsesResult.response.status_response === 'OK')) return;
      const evaluationResponses = responsesResult.response.data || [];
      setCurrentApplicants(prev => prev.map(app => {
        const evaluationResponse = evaluationResponses.find(r => r.applicantId === app.applicantId);
        if (!evaluationResponse) return app;
        return {
          ...app,
          status: evaluationResponse.overallStatus === 'completed' ? 'completed' : app.status,
          responseId: evaluationResponse.id,
          ...extractProcessingFields(evaluationResponse)
        };
      }));
    } catch (err) {
      console.log('[assessment2] No se pudo refrescar el estado de procesamiento:', err);
    }
  };

  // Mientras haya respuestas procesándose en la cola, refrescar cada 20 s.
  const hasProcessing = currentApplicants.some(app => app.processingStatus === 'processing');
  useEffect(() => {
    if (!hasProcessing) return undefined;
    const interval = setInterval(refreshResponses, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProcessing, evaluation?.id]);

  // Modificar la función loadInitialData
  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Cargar applicants disponibles
      const applicantsResponse = await getAllApplicants();
      if (applicantsResponse.response && applicantsResponse.response.status_response === 'OK') {
        const unique = Object.values(
          (applicantsResponse.response.data || []).reduce((acc, app) => {
            const key = app.email || app.id;
            if (!acc[key]) acc[key] = app;
            return acc;
          }, {})
        );
        setApplicants(unique.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));

        // Cargar evaluationResponses para obtener responseId
        let evaluationResponses = [];
        try {
          const responsesResult = await getEvaluationResponses(evaluation.id);
          if (responsesResult.response && responsesResult.response.status_response === 'OK') {
            evaluationResponses = responsesResult.response.data || [];
          }
        } catch (err) {
          console.log('No se pudieron cargar evaluationResponses:', err);
        }

        // Enriquecer currentApplicants con datos completos incluyendo RUT y responseId
        const enrichedCurrentApplicants = (evaluation.applicants || []).map(evalApplicant => {
          const fullApplicant = applicantsResponse.response.data.find(app => app.id === evalApplicant.applicantId);

          // Buscar responseId correspondiente
          const evaluationResponse = evaluationResponses.find(response =>
            response.applicantId === evalApplicant.applicantId
          );

          return {
            ...evalApplicant,
            rut: fullApplicant?.rut || '',
            phone: fullApplicant?.phone || '',
            responseId: evaluationResponse?.id || null,
            ...extractProcessingFields(evaluationResponse)
          };
        });

        setCurrentApplicants(enrichedCurrentApplicants);
      }

    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(t('evaluationResponses.errorLoadingInitialData'));
    } finally {
      setLoading(false);
    }
  };

  // Función para agregar candidato
  const handleAddApplicant = async () => {
    if (!selectedApplicant) {
      showAlert('Por favor selecciona un candidato', 'warning');
      return;
    }

    try {
      // Verificar si ya está agregado
      if (currentApplicants.some(candidate => candidate.applicantId === selectedApplicant.id)) {
        showAlert('Este candidato ya ha sido agregado', 'warning');
        return;
      }

      // Datos que espera el backend
      const applicantData = {
        applicantId: selectedApplicant.id,
        name: selectedApplicant.name,
        email: selectedApplicant.email
      };

      const response = await addApplicantToEvaluation(evaluation.id, applicantData);

      if (response && response.response && response.response.status_response === 'OK') {
        const backendData = response.response.data;

        const newApplicant = {
          applicantId: backendData?.applicantId || selectedApplicant.id,
          name: backendData?.name || selectedApplicant.name,
          email: backendData?.email || selectedApplicant.email,
          status: backendData?.status || 'pending',
          invitedAt: backendData?.invitedAt || null,
          firstOpenedAt: backendData?.firstOpenedAt || null,
          startedAt: backendData?.startedAt || null,
          completedAt: backendData?.completedAt || null,
          isExpired: backendData?.isExpired || false
        };

        setCurrentApplicants(prev => [...prev, newApplicant]);
        setSelectedApplicant(null);
        showAlert('Candidato agregado exitosamente', 'success');
      } else {
        console.log('Respuesta inesperada:', response);
        showAlert('Error en la respuesta del servidor', 'error');
      }

    } catch (err) {
      console.error('Error adding applicant:', err);

      if (err.response) {
        const statusCode = err.response.status;
        const errorData = err.response.data;

        if (statusCode === 400 && errorData.response && errorData.response.message) {
          showAlert(errorData.response.message, 'error');
        } else {
          showAlert(`Error del servidor (${statusCode})`, 'error');
        }
      } else if (err.request) {
        showAlert('Error de conexión con el servidor', 'error');
      } else {
        showAlert('Error inesperado', 'error');
      }
    }
  };

  // Función para generar evaluación individual - CON ENVÍO DE EMAIL
  const handleGenerateEvaluation = async (applicantId) => {
    try {
      setSendingInvitations(prev => ({ ...prev, [applicantId]: true }));

      const evaluationResponseData = {
        evaluationId: evaluation.id,
        applicantId: applicantId,
        creatorEmail: user?.email,
        projectId: project,
        organizationId: organization
      };

      const responseCreated = await createEvaluationResponse(evaluationResponseData);

      if (responseCreated.response && responseCreated.response.status_response === 'OK') {
        const applicant = applicants.find(app => app.id === applicantId);

        const emailData = {
          candidateEmail: applicant?.email || '',
          creatorEmail: user?.email || 'support@amandahr.ai',
          positionName: evaluation.positionName || 'Sin especificar',
          candidateName: applicant?.name || 'Candidato',
          language: language || 'es',
          projectId: project,
          organizationId: organization
        };

        try {
          const emailResponse = await api.post(
            `/assessment2/evaluation-responses/${responseCreated.response.data.id}/send-email`,
            emailData,
            {
              headers: { Authorization: `Bearer ${authToken}` }
            }
          );

          if (emailResponse.data.errorCode === 0) {
            console.log('Email enviado exitosamente');
          } else {
            console.error('Error al enviar email:', emailResponse.data.errorMessage);
          }
        } catch (emailError) {
          console.error('Error al enviar email de invitación:', emailError);
        }

        const updateResponse = await sendInvitationAPI(evaluation.id, applicantId);

        if (updateResponse.response && updateResponse.response.status_response === 'OK') {
          const updatedApplicantData = updateResponse.response.data;

          setCurrentApplicants(prev =>
            prev.map(app =>
              app.applicantId === applicantId
                ? {
                  ...app,
                  status: updatedApplicantData.status,
                  invitedAt: updatedApplicantData.invitedAt,
                  responseId: responseCreated.response.data.id
                }
                : app
            )
          );

          const questionsCount = responseCreated.response.data.questions?.length || 0;
          showAlert(t('evaluationResponses.evaluationGeneratedSuccessfully', {
            questionsCount,
            responseId: responseCreated.response.data.id
          }), 'success');
        } else {
          showAlert(t('evaluationResponses.evaluationCreatedButErrorUpdatingApplicant'), 'warning');
        }
      } else {
        showAlert(t('evaluationResponses.errorCreatingEvaluation'), 'error');
      }

    } catch (err) {
      console.error('Error generating evaluation:', err);
      showAlert(t('evaluationResponses.errorGeneratingEvaluation'), 'error');
    } finally {
      setSendingInvitations(prev => ({ ...prev, [applicantId]: false }));
    }
  };

  // Función para generar evaluaciones para todos
  const handleGenerateAllEvaluations = async () => {
    try {
      setSaving(true);

      const pendingApplicants = currentApplicants.filter(app => app.status === 'pending');
      let successCount = 0;
      let errorCount = 0;

      for (const applicant of pendingApplicants) {
        try {
          await handleGenerateEvaluation(applicant.applicantId);
          successCount++;
        } catch (error) {
          console.error(`Error generando evaluación para ${applicant.name}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        showAlert(t('evaluationResponses.evaluationsGeneratedSuccessfully', { count: successCount }), 'success');
      }

      if (errorCount > 0) {
        showAlert(t('evaluationResponses.evaluationsFailed', { count: errorCount }), 'warning');
      }

    } catch (err) {
      console.error('Error generating all evaluations:', err);
      showAlert(t('evaluationResponses.errorGeneratingEvaluations'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResendEmail = async (applicantId, responseId) => {
    try {
      setResendingEmail(prev => ({ ...prev, [applicantId]: true }));

      const applicant = currentApplicants.find(app => app.applicantId === applicantId);

      if (!applicant) {
        showAlert(t('evaluationResponses.applicantNotFound'), 'error');
        return;
      }

      const { user } = useStore.getState();

      const emailData = {
        candidateEmail: applicant.email || '',
        creatorEmail: user?.email || 'support@amandahr.ai',
        positionName: evaluation.positionName || 'Sin especificar',
        candidateName: applicant.name || 'Candidato',
        language: language || 'es',
        organizationId: organization,
        projectId: project
      };

      const emailResponse = await api.post(
        `/assessment2/evaluation-responses/${responseId}/send-email`,
        emailData,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (emailResponse.data.errorCode === 0) {
        showAlert(t('evaluationResponses.emailResentSuccessfully'), 'success');
      } else {
        showAlert(t('evaluationResponses.errorResendingEmail'), 'error');
      }

    } catch (err) {
      console.error('Error resending email:', err);
      showAlert(t('evaluationResponses.errorResendingEmail'), 'error');
    } finally {
      setResendingEmail(prev => ({ ...prev, [applicantId]: false }));
    }
  };

  // Función para ver resultados del assessment
  const handleViewResults = async (responseId) => {
    try {
      const response = await api.get(
        `/assessment2/evaluation-responses/${responseId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0 && response.data.response?.status_response === 'OK') {
        setSelectedResult(response.data.response.data);
        setResultModalOpen(true);
      } else {
        showAlert('Error al cargar los resultados', 'error');
      }
    } catch (err) {
      console.error('Error loading assessment results:', err);
      showAlert('Error al cargar los resultados del assessment', 'error');
    }
  };

  // Reprocesar assessment: vuelve a correr el scoring con IA y regenera PDF y Word.
  // Sólo disponible para super-admin y sobre assessments ya completados.
  const handleReprocess = async () => {
    if (!applicantToReprocess) return;

    const { responseId } = applicantToReprocess;
    setApplicantToReprocess(null);
    setReprocessingId(responseId);

    try {
      const response = await api.post(
        `/assessment2/evaluation-responses/${responseId}/reprocess`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        const queued = response.data.response?.queued === true;
        showAlert(queued ? t('assessment2.reprocessQueued') : t('evaluationResponses.reprocessSuccess'), 'success');
        if (queued) {
          // Marcar localmente como procesando; el refresco periódico traerá el resultado.
          setCurrentApplicants(prev => prev.map(app =>
            app.responseId === responseId
              ? { ...app, processingStatus: 'processing', processingError: null, hasEvaluation: false }
              : app
          ));
        } else {
          await loadInitialData();
        }
      } else {
        showAlert(t('evaluationResponses.reprocessError'), 'error');
      }
    } catch (err) {
      console.error('Error reprocessing assessment:', err);
      showAlert(t('evaluationResponses.reprocessError'), 'error');
    } finally {
      setReprocessingId(null);
    }
  };

  // Función para obtener el color del chip según el estado
  const getStatusChip = (status) => {
    const statusConfig = {
      pending: {
        label: t('evaluationResponses.statusLabels.pending'),
        color: 'warning',
        tooltip: t('evaluationResponses.statusTooltips.pending')
      },
      sent: {
        label: t('evaluationResponses.statusLabels.sent'),
        color: 'info',
        tooltip: t('evaluationResponses.statusTooltips.sent')
      },
      opened: {
        label: t('evaluationResponses.statusLabels.opened'),
        color: 'primary',
        tooltip: t('evaluationResponses.statusTooltips.opened')
      },
      started: {
        label: t('evaluationResponses.statusLabels.started'),
        color: 'secondary',
        tooltip: t('evaluationResponses.statusTooltips.started')
      },
      completed: {
        label: t('evaluationResponses.statusLabels.completed'),
        color: 'success',
        tooltip: t('evaluationResponses.statusTooltips.completed')
      },
      expired: {
        label: t('evaluationResponses.statusLabels.expired'),
        color: 'error',
        tooltip: t('evaluationResponses.statusTooltips.expired')
      }
    };

    const config = statusConfig[status] || {
      label: status,
      color: 'default',
      tooltip: t('evaluationResponses.statusTooltips.unknown')
    };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        title={config.tooltip}
        sx={{ cursor: 'help' }}
      />
    );
  };

  // Función para formatear fechas
  const formatDate = (dateValue) => {
    if (!dateValue || dateValue === null || dateValue === undefined) return '-';

    try {
      let date;

      if (dateValue && typeof dateValue === 'object' && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      }
      else if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      }
      else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) return '-';

      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('Error formatting date:', error, dateValue);
      return '-';
    }
  };

  // Función para copiar URL de evaluación pública
  const handleCopyEvaluationUrl = async (responseId) => {
    try {
      const url = `https://app.amandahr.ai/assessment2-public/${responseId}/${language}`;
      await navigator.clipboard.writeText(url);
      showAlert('URL copiada al portapapeles', 'success');
    } catch (err) {
      console.error('Error copying URL:', err);
      showAlert('Error al copiar URL', 'error');
    }
  };

  // Filtrar candidatos disponibles
  const availableCandidates = applicants.filter(applicant =>
    !currentApplicants.some(current => current.applicantId === applicant.id)
  ).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));

  if (!evaluation) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{t('evaluationResponses.evaluationNotFound')}</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          {t('evaluationResponses.goBack')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={onBack}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('evaluationResponses.evaluations')}
        </Link>
        <Typography color="text.primary">{evaluation.title}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" color="text.secondary">
            {evaluation.title} - {evaluation.positionName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            {t('evaluationResponses.goBack')}
          </Button>

          <Button
            variant="contained"
            startIcon={<BarChartIcon />}
            onClick={onViewComparison}
            color="success"
          >
            {t('evaluationResponses.viewDashboard')}
          </Button>
        </Box>
      </Box>

      {/* Información de la evaluación */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ mr: 1 }} />
          {t('evaluationResponses.evaluationInformation')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('evaluationResponses.title')}:</strong> {evaluation.title}
            </Typography>
            {evaluation.description && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t('evaluationResponses.description')}:</strong> {evaluation.description}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('evaluationResponses.position')}:</strong> {evaluation.positionName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('evaluationResponses.competencies')}:</strong> {evaluation.capabilities?.length || 0}
            </Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon sx={{ mr: 0.5, fontSize: 16 }} />
              <strong>{t('evaluationResponses.timeLimit')}:</strong> {evaluation.timeLimit} {t('evaluationResponses.minutes')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Alert de límites de assessments */}
      <EvaluationAlert
        pendingCount={pendingCount}
        showDetailedInfo={pendingCount > 0}
        onUsageCheckComplete={handleUsageCheckComplete}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Sección para agregar candidatos */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <AddIcon sx={{ mr: 1 }} />
              {t('evaluationResponses.addApplicants')}
            </Typography>

            {availableCandidates.length === 0 ? (
              <Alert severity="info">
                {t('evaluationResponses.noApplicantsAvailable')}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Autocomplete
                  options={availableCandidates}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedApplicant}
                  onChange={(event, newValue) => setSelectedApplicant(newValue)}
                  sx={{ minWidth: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('evaluationResponses.selectApplicant')}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                      </Box>
                    </li>
                  )}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddApplicant}
                  disabled={!selectedApplicant}
                >
                  {t('evaluationResponses.add')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Tabla de candidatos */}
          <Paper>
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ mr: 1 }} />
                {t('evaluationResponses.applicantsCount', { count: currentApplicants.length })}
              </Typography>

              {currentApplicants.some(app => app.status === 'pending') && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
                  onClick={handleGenerateAllEvaluations}
                  disabled={saving || !canSendToAll}
                >
                  {t('evaluationResponses.generateEvaluationsForAllPending')}
                </Button>
              )}
            </Box>

            <Divider />

            {currentApplicants.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('evaluationResponses.noApplicantsAdded')}
                </Typography>
                <Typography color="text.secondary">
                  {t('evaluationResponses.addApplicantsToStart')}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('evaluationResponses.name')}</strong></TableCell>
                      <TableCell><strong>{t('evaluationResponses.email')}</strong></TableCell>
                      <TableCell><strong>{t('evaluationResponses.status')}</strong></TableCell>
                      <TableCell><strong>{t('evaluationResponses.evaluationGenerated')}</strong></TableCell>
                      <TableCell><strong>{t('evaluationResponses.lastActivity')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('evaluationResponses.actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentApplicants.map((applicant) => (
                      <StyledTableRow key={applicant.applicantId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {applicant.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {applicant.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(applicant.status)}
                          {applicant.status === 'completed' && applicant.processingStatus === 'processing' && (
                            <Tooltip title={t('assessment2.processingTooltip')}>
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                icon={<CircularProgress size={12} />}
                                label={t('assessment2.processing')}
                                sx={{ ml: 1 }}
                              />
                            </Tooltip>
                          )}
                          {applicant.status === 'completed' && applicant.processingStatus === 'error' && (
                            <Tooltip title={applicant.processingError || t('assessment2.processingErrorTooltip')}>
                              <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label={t('assessment2.processingError')}
                                sx={{ ml: 1 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(applicant.invitedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(applicant.completedAt || applicant.startedAt || applicant.firstOpenedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {applicant.status === 'pending' && (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleGenerateEvaluation(applicant.applicantId)}
                              disabled={sendingInvitations[applicant.applicantId]}
                              title={t('evaluationResponses.generateEvaluation')}
                              sx={{ mr: 1 }}
                            >
                              {sendingInvitations[applicant.applicantId] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <GenerateIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                          {applicant.status === 'completed' && applicant.responseId && applicant.hasEvaluation && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewResults(applicant.responseId)}
                              title="Ver Resultados"
                              sx={{ mr: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                          {/* REPROCESAR - Sólo super-admin, sólo assessments completados */}
                          {user?.rol === 'super-admin' && applicant.status === 'completed' && applicant.responseId && (
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => setApplicantToReprocess(applicant)}
                              disabled={reprocessingId !== null || applicant.processingStatus === 'processing'}
                              title={t('reprocess')}
                              sx={{ mr: 1 }}
                            >
                              {reprocessingId === applicant.responseId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ReplayIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                          {['sent', 'opened'].includes(applicant.status) && applicant.responseId && (
                            <>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleCopyEvaluationUrl(applicant.responseId)}
                                title={t('evaluationResponses.copyEvaluationUrl')}
                                sx={{ mr: 1 }}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleResendEmail(applicant.applicantId, applicant.responseId)}
                                disabled={resendingEmail[applicant.applicantId]}
                                title={t('evaluationResponses.resendEmail')}
                                sx={{ mr: 1 }}
                              >
                                {resendingEmail[applicant.applicantId] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <EmailIcon fontSize="small" />
                                )}
                              </IconButton>
                            </>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteConfirm(applicant)}
                            title={t('evaluationResponses.deleteApplicant')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Modal de confirmación para eliminar candidato */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="delete-confirm-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="delete-confirm-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'error.main',
            pb: 1
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          {t('evaluationResponses.confirmDeletion')}
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('evaluationResponses.deleteConfirmationMessage', {
              name: candidateToDelete?.name || t('evaluationResponses.noName')
            })}
          </DialogContentText>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('evaluationResponses.thisAction')}:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              • {t('evaluationResponses.deleteActionRemove')}
              <br />
              • {t('evaluationResponses.deleteActionPermanent')}
              <br />
              • <strong>{t('evaluationResponses.deleteActionUndoable')}</strong>
            </Typography>
          </Alert>

          {candidateToDelete && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('evaluationResponses.applicant')}:</strong> {candidateToDelete.name || t('evaluationResponses.noName')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('evaluationResponses.email')}:</strong> {candidateToDelete.email || t('evaluationResponses.noEmail')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('evaluationResponses.status')}:</strong> {candidateToDelete.status || 'pending'}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDeleteConfirm}
            color="inherit"
            variant="outlined"
          >
            {t('evaluationResponses.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            {t('evaluationResponses.yesDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Resultados del Assessment */}
      <AssessmentResultModal
        open={resultModalOpen}
        onClose={() => setResultModalOpen(false)}
        data={selectedResult}
      />

      {/* Confirmación de reproceso: pisa score, conclusión y reportes */}
      <Dialog
        open={Boolean(applicantToReprocess)}
        onClose={() => setApplicantToReprocess(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('evaluationResponses.reprocessTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('evaluationResponses.reprocessConfirm', {
              name: applicantToReprocess?.name || applicantToReprocess?.applicantName || ''
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApplicantToReprocess(null)}
            color="inherit"
            variant="outlined"
          >
            {t('evaluationResponses.cancel')}
          </Button>
          <Button
            onClick={handleReprocess}
            color="warning"
            variant="contained"
            startIcon={<ReplayIcon />}
            autoFocus
          >
            {t('reprocess')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationView;